/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAssistantExecutionProfilesRequest,
  ListAssistantExecutionProfilesSecurity,
} from "../models/operations/listassistantexecutionprofiles.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AssistantExecutionProfilesQueryData,
  buildAssistantExecutionProfilesQuery,
  prefetchAssistantExecutionProfiles,
  queryKeyAssistantExecutionProfiles,
} from "./assistantExecutionProfiles.core.js";
export {
  type AssistantExecutionProfilesQueryData,
  buildAssistantExecutionProfilesQuery,
  prefetchAssistantExecutionProfiles,
  queryKeyAssistantExecutionProfiles,
};

export type AssistantExecutionProfilesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listExecutionProfiles assistants
 *
 * @remarks
 * List the upstream credentials an assistant's turns use on each MCP server it reaches, per root trigger and per whether the turn acts for a user. Each server shows its current choice, or why it has none, and the credentials that can be chosen. Requires assistant read access.
 */
export function useAssistantExecutionProfiles(
  request: ListAssistantExecutionProfilesRequest,
  security?: ListAssistantExecutionProfilesSecurity | undefined,
  options?: QueryHookOptions<
    AssistantExecutionProfilesQueryData,
    AssistantExecutionProfilesQueryError
  >,
): UseQueryResult<
  AssistantExecutionProfilesQueryData,
  AssistantExecutionProfilesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAssistantExecutionProfilesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listExecutionProfiles assistants
 *
 * @remarks
 * List the upstream credentials an assistant's turns use on each MCP server it reaches, per root trigger and per whether the turn acts for a user. Each server shows its current choice, or why it has none, and the credentials that can be chosen. Requires assistant read access.
 */
export function useAssistantExecutionProfilesSuspense(
  request: ListAssistantExecutionProfilesRequest,
  security?: ListAssistantExecutionProfilesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AssistantExecutionProfilesQueryData,
    AssistantExecutionProfilesQueryError
  >,
): UseSuspenseQueryResult<
  AssistantExecutionProfilesQueryData,
  AssistantExecutionProfilesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAssistantExecutionProfilesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAssistantExecutionProfilesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      assistantId: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: AssistantExecutionProfilesQueryData,
): AssistantExecutionProfilesQueryData | undefined {
  const key = queryKeyAssistantExecutionProfiles(...queryKeyBase);

  return client.setQueryData<AssistantExecutionProfilesQueryData>(key, data);
}

export function invalidateAssistantExecutionProfiles(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      assistantId: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "assistants",
      "listExecutionProfiles",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAssistantExecutionProfiles(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "assistants", "listExecutionProfiles"],
  });
}
